import { useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { brandVars } from "./brands";
import { BrandMark } from "./BrandMark";
import { SpaceSwitcher } from "./SpaceSwitcher";
import { usePortal } from "./state";
import { openTicketCount } from "./tickets";

// Two nav groups (COMPONENTS.md NavTabs): the primary group on the left, the
// utility group pushed right with margin-left:auto so it wraps as a unit.
const PRIMARY = [
  { seg: "home", label: "Home" },
  { seg: "guides", label: "Guides" },
  { seg: "projects", label: "Projects" },
  { seg: "onboarding", label: "Onboarding" },
  { seg: "developers", label: "Developers" },
  { seg: "community", label: "Community" },
] as const;

const UTILITY = [
  { seg: "downloads", label: "Downloads" },
  { seg: "releases", label: "Release notes" },
  { seg: "status", label: "Status", dot: true },
  { seg: "tickets", label: "My tickets", count: true },
] as const;

export function PortalShell() {
  const { brand, brandId, user, signOut } = usePortal();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [q, setQ] = useState("");
  // Derived from the ticket store so the header chip, nav badge and the list all
  // agree (they used to disagree — hardcoded demo count vs computed list).
  const openTickets = openTicketCount(brandId);

  const on = "#fff";
  const activeSeg = pathname.replace(/^\/portal\//, "").split("/")[0] || "home";
  // Article lives under Guides; ask/search under Home; ticket/new-ticket under My
  // tickets (mirrors the prototype's viewGroup).
  const group =
    activeSeg === "article" ? "guides"
    : activeSeg === "ask" ? "home"
    : activeSeg === "ticket" || activeSeg === "new-ticket" ? "tickets"
    : activeSeg === "project" ? "projects"
    : activeSeg;

  function submitSearch() {
    const query = q.trim();
    navigate({ to: "/portal/ask", search: query ? { q: query } : {} });
  }

  return (
    <div style={{ ...brandVars(brand), height: "100vh", display: "grid", gridTemplateRows: "56px auto minmax(0,1fr)", overflow: "hidden", background: "#f4f3ef", fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* HEADER */}
      <header style={{ background: "var(--sf)", display: "flex", alignItems: "center", gap: 12, padding: "0 18px", zIndex: 8 }}>
        <Link to="/portal/home" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 11, flex: "none" }}>
          <BrandMark brand={brand} on={on} variant="header" />
          <span style={{ fontWeight: 500, fontSize: 9.5, letterSpacing: 1.8, textTransform: "uppercase", color: "var(--on3)" }}>Help</span>
        </Link>

        <div style={{ width: 1, height: 22, background: "var(--onBg2)", flex: "none" }} />

        <SpaceSwitcher />

        <div style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
          <div style={{ position: "relative", width: "100%", maxWidth: 420 }}>
            <span style={{ position: "absolute", left: 10, top: 8 }}>
              <Icon name="search" size={15} color="var(--on3)" />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="Ask a question or search"
              style={{ width: "100%", padding: "8px 12px 8px 32px", border: "1px solid var(--onBd)", background: "var(--onBg)", borderRadius: 3, fontSize: 13, color: "var(--on)", outline: 0, fontFamily: "inherit" }}
            />
          </div>
        </div>

        <Link to="/portal/tickets" title="My tickets" style={{ textDecoration: "none", border: 0, background: "var(--onBg)", borderRadius: 3, height: 30, padding: "0 10px", display: "flex", alignItems: "center", gap: 7, flex: "none" }}>
          <Icon name="ticket" size={15} color="var(--on)" />
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--on)", fontFamily: "'Space Mono', monospace" }}>{openTickets}</span>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "none" }}>
          <div style={{ width: 29, height: 29, borderRadius: 3, background: "var(--onBg2)", color: "var(--on)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, letterSpacing: ".3px" }}>{user.initials}</div>
        </div>
        <button
          onClick={() => {
            signOut();
            navigate({ to: "/portal" });
          }}
          title="Sign out"
          style={{ border: 0, background: "transparent", padding: 4, display: "flex", flex: "none", cursor: "pointer" }}
        >
          <Icon name="log-out" size={15} color="var(--on3)" />
        </button>
      </header>

      {/* NAV */}
      <nav style={{ background: "#fff", borderBottom: "1px solid #e6e4dc", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, padding: "0 18px", flexWrap: "wrap", zIndex: 6 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, minWidth: 0 }}>
          {PRIMARY.map((t) => (
            <NavTab key={t.seg} seg={t.seg} label={t.label} active={group === t.seg} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, minWidth: 0, marginLeft: "auto", justifyContent: "flex-end" }}>
          {UTILITY.map((t) => (
            <NavTab
              key={t.seg}
              seg={t.seg}
              label={t.label}
              active={group === t.seg}
              dot={"dot" in t && t.dot ? "#1f8a5b" : undefined}
              count={"count" in t && t.count ? openTickets : undefined}
            />
          ))}
        </div>
      </nav>

      <main style={{ overflowY: "auto", minHeight: 0 }}>
        <Outlet />
      </main>
    </div>
  );
}

function NavTab({ seg, label, active, dot, count }: { seg: string; label: string; active: boolean; dot?: string; count?: number }) {
  return (
    <Link
      to={`/portal/${seg}`}
      style={{
        textDecoration: "none",
        border: 0,
        background: "transparent",
        padding: "11px 11px 9px",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? "var(--bi)" : "#4a504a",
        borderBottom: `2px solid ${active ? "var(--bi)" : "transparent"}`,
        marginBottom: -1,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {dot && <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flex: "none" }} />}
      {label}
      {count != null && (
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 700, background: "#f0efe9", color: "#4a504a", borderRadius: 2, padding: "1px 5px" }}>{count}</span>
      )}
    </Link>
  );
}
